import { Loader2, PenLine } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	dayKindLabel,
	isFutureDate,
	STATUS_LABELS,
} from "@/features/presence/utils/attendance";
import { DayEditor } from "@/features/team/components/day-editor";
import { currentWorkDate } from "@/lib/format";
import { useWorkerMonthDays } from "../../hooks/use-worker-month-days";
import type { TimesheetDay, TimesheetRow } from "../../types";
import {
	dayNumberLabel,
	formatHoursMinutes,
	formatPct,
	monthLabel,
	weekdayShortLabel,
} from "../../utils/format";

function hasOvertime(day: TimesheetDay): boolean {
	return day.overtimeMinutes > 0;
}

export function WorkerDayDrawer({
	open,
	onOpenChange,
	row,
	month,
	holidays,
	canUpdate,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	row: TimesheetRow | undefined;
	month: string;
	holidays: Map<string, string>;
	canUpdate: boolean;
}) {
	const [editingDate, setEditingDate] = useState<string | undefined>();
	const [editorOpen, setEditorOpen] = useState(false);
	const monthDays = useWorkerMonthDays(row?.worker.id, month, open);
	const today = currentWorkDate();

	const editableDay = editingDate
		? monthDays.data?.days.find((day) => day.date === editingDate)
		: undefined;

	function openEditor(date: string) {
		setEditingDate(date);
		setEditorOpen(true);
	}

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="worker-day-drawer">
				<SheetHeader>
					<SheetTitle>{row?.worker.name ?? "Colaborador"}</SheetTitle>
					<SheetDescription>
						{[row?.worker.jobTitle, monthLabel(month)]
							.filter(Boolean)
							.join(" · ")}
					</SheetDescription>
				</SheetHeader>

				<div className="worker-day-drawer__body">
					{row && (
						<table className="worker-days">
							<thead>
								<tr>
									<th>Data</th>
									<th>Dia</th>
									<th>Status</th>
									<th>Trab.</th>
									<th>Extras</th>
									{canUpdate && <th className="visually-hidden">Ações</th>}
								</tr>
							</thead>
							<tbody>
								{row.days.map((day) => {
									const future = isFutureDate(day.date, today);
									const editable =
										canUpdate &&
										!future &&
										monthDays.data?.days.some((d) => d.date === day.date);
									return (
										<tr
											key={day.date}
											data-date={day.date}
											data-kind={day.kind}
											data-status={day.status}
											data-overtime={hasOvertime(day) ? "true" : undefined}
										>
											<td className="worker-days__date">
												<span className="worker-days__day-number">
													{dayNumberLabel(day.date)}
												</span>
												<span className="worker-days__weekday">
													{weekdayShortLabel(day.date)}
												</span>
											</td>
											<td className="worker-days__kind">
												{dayKindLabel(day.kind, holidays.get(day.date))}
											</td>
											<td className="worker-days__status">
												<Badge variant="secondary" data-status={day.status}>
													{STATUS_LABELS[day.status]}
												</Badge>
											</td>
											<td className="worker-days__minutes">
												{formatHoursMinutes(day.workedMinutes)}
											</td>
											<td className="worker-days__overtime">
												{hasOvertime(day) ? (
													<>
														<span>
															{formatHoursMinutes(day.overtimeMinutes)}
														</span>
														<span className="worker-days__pct">
															{formatPct(day.overtimePct)}
															{day.overridden && " · ajustado"}
														</span>
													</>
												) : (
													<span className="worker-days__empty">—</span>
												)}
											</td>
											{canUpdate && (
												<td className="worker-days__actions">
													<Button
														type="button"
														variant="ghost"
														size="sm"
														aria-label={`Editar dia ${dayNumberLabel(day.date)}`}
														disabled={!editable}
														onClick={() => openEditor(day.date)}
													>
														<PenLine />
														Editar dia
													</Button>
												</td>
											)}
										</tr>
									);
								})}
							</tbody>
						</table>
					)}
					{row && canUpdate && monthDays.isPending && (
						<p className="worker-day-drawer__state">
							<Loader2 className="worker-day-drawer__spinner" />
							Carregando os dias para edição…
						</p>
					)}
					{row && canUpdate && monthDays.isError && (
						<p className="worker-day-drawer__state is-error">
							Não foi possível carregar os dias para edição.
						</p>
					)}
				</div>

				<DayEditor
					open={editorOpen}
					onOpenChange={setEditorOpen}
					worker={monthDays.data?.worker}
					day={editableDay}
					canUpdate={canUpdate}
				/>
			</SheetContent>
		</Sheet>
	);
}
